import './style.scss';

import { HISTORY_TRANSIFY_KEY, TRACKING_MSG_TYPE, TRACKING_OPERATION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/constants';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { getTrans, openFullScreenImage } from '@/utils/tools';
import { inject, observer } from 'mobx-react';

import FailToLoad from '@/assets/img/fail_to_load.svg';
import clsx from 'classnames';
import { dispatchTrack } from '@/utils/tracking';
import { getChildInject } from '@/utils/instantLoaded/tools';

export interface HistoryImageProps {
  /** 原图url */
  url: string;
  /** 缩略图url */
  thumbnail: string;
  initStore: any;
  from: string;
}

const RELOAD_TRACKING_NAME = TRACKING_TYPE.RELOAD_IMAGE;
const IMAGE_TRACKING_NAME = TRACKING_TYPE.IMAGE;

function HistoryImage(props: HistoryImageProps) {
  const { thumbnail } = props;
  // 随机数用于重新加载图片
  const [random, setRandom] = useState(Date.now().toString());
  const [hasError, setHasError] = useState(false);
  const [hasLoad, setHasLoad] = useState(false);
  const errorRef = useRef(null);
  const imageRef = useRef(null);
  const reloadTrackingData = {
    current_chat_session_id: props.initStore.sessionId,
    image_id: thumbnail
  };
  const imageTrackingData = {
    chat_session_id: props.initStore.sessionId,
    if_history: TRACKING_MSG_TYPE.HISTORY,
    image_id: thumbnail,
    is_sender: props.from === 'user' ? 'true' : 'false'
  };
  const imageUrl = useMemo(() => {
    const appendToken = thumbnail.includes('?') ? '&' : '?';
    return `${thumbnail}${appendToken}t=${random}`;
  }, [thumbnail, random]);

  const onError = () => {
    setHasError(true);
  };

  const onLoad = () => {
    setHasLoad(true);
  };

  const onReload = () => {
    dispatchTrack({
      operation: TRACKING_OPERATION.CLICK,
      target: TRACKING_TARGET[RELOAD_TRACKING_NAME],
      data: reloadTrackingData,
    });
    setHasError(false);
    setRandom(Date.now().toString());
  };

  const onClick = () => {
    if (hasLoad) {
      dispatchTrack({
        operation: TRACKING_OPERATION.CLICK,
        target: TRACKING_TARGET[IMAGE_TRACKING_NAME],
        data: imageTrackingData,
      });
      openFullScreenImage(props.url);
    }
  };

  useEffect(() => {
    if (hasError) {
      addObserver(errorRef?.current);
      removeObserver(imageRef?.current);
    } else {
      addObserver(imageRef?.current);
      removeObserver(errorRef.current);
    }

    return () => {
      removeObserver(imageRef?.current);
      removeObserver(errorRef?.current);
    };
  }, [hasError]);

  return (
    <div className="history-image">
      <div className="history-image__content">
        {
          hasError
            ? <div
              ref={errorRef}
              className="history-image__error"
              data-trackingname={RELOAD_TRACKING_NAME}
              data-trackingdata={JSON.stringify(reloadTrackingData)}
            >
              <img className="error-image" src={FailToLoad} />
              <div className="error-prompt">
                <div>{getTrans(HISTORY_TRANSIFY_KEY.FAIL_TO_LOAD_IMAGE)}</div>
                <div className="error-prompt__btn">
                  <a onClick={onReload}>{getTrans(HISTORY_TRANSIFY_KEY.RELOAD)}</a>.
                </div>
              </div>
            </div>
            : <img
              ref={imageRef}
              data-trackingname={IMAGE_TRACKING_NAME}
              data-trackingdata={JSON.stringify(imageTrackingData)}
              className={clsx({ 'white-bg': hasLoad })}
              src={imageUrl}
              onLoad={onLoad}
              onError={onError}
              onClick={onClick}
            />
        }
      </div>
    </div>
  );
}

export default inject(getChildInject('initStore'))(observer(HistoryImage));
